import React, { useState, useCallback, useRef, useEffect } from 'react';
import './DataTable.css';

export type CellType = 'text' | 'toggle' | 'dropdown' | 'custom';

export interface DataTableColumn<T> {
	key: string;
	header: string;
	cellType?: CellType;
	render?: (row: T, rowIndex: number) => React.ReactNode;
	width?: string;
	minWidth?: number;
}

export interface DataTableProps<T> {
	columns: DataTableColumn<T>[];
	data: T[];
	maxHeight?: string;
	className?: string;
	ariaLabel?: string;
	keyExtractor?: (row: T, index: number) => string;
	resizable?: boolean;
}

export function DataTable<T>({
	columns,
	data,
	maxHeight,
	className = '',
	ariaLabel,
	keyExtractor,
	resizable = false,
}: DataTableProps<T>) {
	const wrapperStyle: React.CSSProperties = maxHeight ? { maxHeight } : {};
	const tableRef = useRef<HTMLTableElement>(null);
	const [columnWidths, setColumnWidths] = useState<number[]>([]);
	const resizingRef = useRef<{
		colIndex: number;
		startX: number;
		startWidth: number;
	} | null>(null);

	useEffect(() => {
		if (resizable && tableRef.current && columnWidths.length === 0) {
			const ths = tableRef.current.querySelectorAll('thead th');
			const widths = Array.from(ths).map((th) => (th as HTMLElement).offsetWidth);
			setColumnWidths(widths);
		}
	}, [resizable, columns, columnWidths.length]);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent, colIndex: number) => {
			if (!resizable) return;
			e.preventDefault();
			const startX = e.clientX;
			const startWidth = columnWidths[colIndex] || 100;

			resizingRef.current = { colIndex, startX, startWidth };

			const handleMouseMove = (moveEvent: MouseEvent) => {
				if (!resizingRef.current) return;
				const diff = moveEvent.clientX - resizingRef.current.startX;
				const minWidth = columns[resizingRef.current.colIndex]?.minWidth ?? 50;
				const newWidth = Math.max(minWidth, resizingRef.current.startWidth + diff);

				setColumnWidths((prev) => {
					const updated = [...prev];
					updated[resizingRef.current!.colIndex] = newWidth;
					return updated;
				});
			};

			const handleMouseUp = () => {
				resizingRef.current = null;
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
			};

			document.body.style.cursor = 'ew-resize';
			document.body.style.userSelect = 'none';
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		},
		[resizable, columnWidths, columns]
	);

	const getThStyle = (col: DataTableColumn<T>, index: number): React.CSSProperties => {
		if (resizable && columnWidths[index]) {
			return { width: `${columnWidths[index]}px` };
		}
		return col.width ? { width: col.width } : {};
	};

	const getTdStyle = (index: number): React.CSSProperties => {
		if (resizable && columnWidths[index]) {
			return { width: `${columnWidths[index]}px` };
		}
		return {};
	};

	return (
		<div
			className={`data-table-wrapper ${className}`.trim()}
			style={wrapperStyle}
			aria-label={ariaLabel}
			role="region"
		>
			<table
				className={`data-table ${resizable ? 'data-table-resizable' : ''}`}
				ref={tableRef}
			>
				<thead>
					<tr>
						{columns.map((col, index) => (
							<th key={col.key} style={getThStyle(col, index)}>
								<div className="th-content">{col.header}</div>
								{resizable && index < columns.length - 1 && (
									<div
										className="column-resize-handle"
										onMouseDown={(e) => handleMouseDown(e, index)}
										role="separator"
										aria-orientation="vertical"
									/>
								)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.map((row, rowIndex) => {
						const key = keyExtractor ? keyExtractor(row, rowIndex) : String(rowIndex);
						return (
							<tr key={key}>
								{columns.map((col, colIndex) => (
									<td key={col.key} style={getTdStyle(colIndex)}>
										{col.render
											? col.render(row, rowIndex)
											: String((row as Record<string, unknown>)[col.key] ?? '')}
									</td>
								))}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
